import { Children, forwardRef, isValidElement, useState } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Avatar.css";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  size?: AvatarSize;
  src?: string;
  alt?: string;
  /** Initials shown when no image (or image fails to load). */
  initials?: string;
  /** Render with the muted neutral palette instead of brand color. */
  muted?: boolean;
  /** Fallback rendered when no `src` and no `initials`. */
  fallback?: ReactNode;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { size = "md", src, alt = "", initials, muted, fallback, className, ...rest },
  ref,
) {
  const [errored, setErrored] = useState(false);
  const showImg = Boolean(src) && !errored;
  return (
    <span
      ref={ref}
      className={cx("go4-avatar", `go4-avatar--${size}`, muted && "go4-avatar--muted", className)}
      {...rest}
    >
      {showImg ? (
        <img className="go4-avatar__img" src={src} alt={alt} onError={() => setErrored(true)} />
      ) : initials ? (
        <span aria-hidden={alt ? undefined : "true"}>{initials.slice(0, 2).toUpperCase()}</span>
      ) : (
        (fallback ?? (
          <svg
            width="60%"
            height="60%"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="9" r="4" />
            <path d="M4 21c2-4 5-6 8-6s6 2 8 6" />
          </svg>
        ))
      )}
    </span>
  );
});

export interface AvatarGroupProps extends HTMLAttributes<HTMLSpanElement> {
  max?: number;
  size?: AvatarSize;
}

export function AvatarGroup({ children, max, size, className, ...rest }: AvatarGroupProps) {
  const childArray = Children.toArray(children).filter(isValidElement);
  const overflow = max != null && childArray.length > max ? childArray.length - max : 0;
  const visible = max != null ? childArray.slice(0, max) : childArray;
  return (
    <span className={cx("go4-avatar-group", className)} {...rest}>
      {visible}
      {overflow > 0 ? (
        <Avatar
          size={size}
          muted
          initials={`+${overflow}`}
          className="go4-avatar-group__overflow"
        />
      ) : null}
    </span>
  );
}
